import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
    LoaderCircle,
    LucideAngularModule,
    Mic,
    MicOff,
    PhoneOff,
    PhoneOutgoing,
} from 'lucide-angular';

import { ContactAvatarComponent } from '@shared/components/contact-avatar/contact-avatar.component';
import { ContactChannelIconsComponent } from '@shared/components/contact-channel-icons/contact-channel-icons.component';
import { VOICE_CHANNELS, VOICE_TYPE } from '@shared/types/channels.types';

import { VoiceCallService } from '../../services/voice-call.service';
import { isWhatsappVoiceCall } from '../../voice.types';

/**
 * Floating call panel shown while an agent takes a transferred WebRTC call: caller
 * identity, live timer and mute / hang-up controls. Mounted at the inbox root so it
 * floats over the conversation list. Visible whenever a call context is set.
 */
@Component({
    selector: 'app-voice-call-bar',
    standalone: true,
    imports: [
        TranslatePipe,
        LucideAngularModule,
        ContactAvatarComponent,
        ContactChannelIconsComponent,
    ],
    templateUrl: './voice-call-bar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'contents' },
})
export class VoiceCallBarComponent {
    private readonly callS = inject(VoiceCallService);

    protected readonly status = this.callS.status;
    protected readonly muted = this.callS.muted;
    protected readonly context = this.callS.context;
    protected readonly isActive = computed(() => this.status() === 'active');
    /** KAN-238: our outbound call is ringing the callee — no leg up yet, hang-up cancels. */
    protected readonly isRinging = this.callS.outboundRinging;
    /** WhatsApp Cloud leg — flagged on the bar so the agent knows which line they're on. */
    protected readonly isWhatsapp = computed(() => isWhatsappVoiceCall(this.context()));
    protected readonly VOICE_CHANNEL = VOICE_CHANNELS.voice;
    protected readonly WA_TYPE = VOICE_TYPE.whatsapp;

    protected readonly MicIcon = Mic;
    protected readonly MicOffIcon = MicOff;
    protected readonly HangupIcon = PhoneOff;
    protected readonly LoaderIcon = LoaderCircle;
    protected readonly PhoneOutgoingIcon = PhoneOutgoing;

    protected readonly elapsed = signal(0);
    private startTs = 0;

    protected readonly elapsedLabel = computed(() => {
        const s = this.elapsed();
        const mm = Math.floor(s / 60)
            .toString()
            .padStart(2, '0');
        const ss = (s % 60).toString().padStart(2, '0');
        return `${mm}:${ss}`;
    });

    constructor() {
        const timer = setInterval(() => {
            if (this.isActive()) {
                if (!this.startTs) this.startTs = Date.now();
                this.elapsed.set(Math.floor((Date.now() - this.startTs) / 1000));
            } else if (this.startTs) {
                this.startTs = 0;
                this.elapsed.set(0);
            }
        }, 1000);
        inject(DestroyRef).onDestroy(() => clearInterval(timer));
    }

    protected toggleMute() {
        this.callS.toggleMute();
    }

    protected hangup() {
        this.callS.hangup();
    }
}
